import {useCallback, useEffect, useRef} from "react";
import {lookupAddressByPostalCode} from "@/lib/yubinbango";

interface UseAddressAutofillOptions {
  /** 郵便番号の入力値（ハイフン・全角混じり可） */
  postalCode: string;
  /** 現在の住所値。手入力済みの住所を上書きしない判定に使う */
  address: string;
  /** 都道府県+市区町村+町域の前置文字列を受け取る */
  onFill: (prefix: string) => void;
}

/**
 * 郵便番号が7桁揃ったら YubinBango データで都道府県〜町域を引き、
 * 住所欄へ前置入力する。
 *
 * - 住所欄が空、または前回の自動入力値のままの場合のみ上書きする
 *   （ホステスが手入力した住所は壊さない）
 * - 見つからない・オフラインは静かにスキップ = 手入力フォールバック
 * - 同一郵便番号の重複 lookup はしない。drawer 再オープン時は resetAutofill() を呼ぶ
 */
export function useAddressAutofill({postalCode, address, onFill}: UseAddressAutofillOptions) {
  const lastFilledRef = useRef<string | null>(null);
  // lookup が「完了」した郵便番号のみ記録する。スケジュール時に記録すると、
  // デバウンス中の編集（8桁目→戻す等）で abort された試行が完了扱いになり、
  // 同じ7桁に戻したとき二度と引き直せなくなる
  const lastSettledRef = useRef<string | null>(null);
  // effect の依存を postalCode のみにするため、最新値は ref 経由で読む
  const addressRef = useRef(address);
  addressRef.current = address;
  const onFillRef = useRef(onFill);
  onFillRef.current = onFill;

  useEffect(() => {
    const digits = postalCode
      .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[^0-9]/g, "");
    if (digits.length !== 7 || digits === lastSettledRef.current) return;

    const current = addressRef.current.trim();
    if (current !== "" && current !== lastFilledRef.current) return;

    const controller = new AbortController();
    // 連続入力の最後の確定だけ飛ばすデバウンス
    const timer = setTimeout(async () => {
      const result = await lookupAddressByPostalCode(digits, controller.signal);
      if (controller.signal.aborted) return;
      lastSettledRef.current = digits;
      // デバウンス〜fetch の間に住所が手入力されていたら上書きしない（再チェック）
      const latest = addressRef.current.trim();
      if (latest !== "" && latest !== lastFilledRef.current) return;
      if (result) {
        lastFilledRef.current = result.prefix;
        onFillRef.current(result.prefix);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [postalCode]);

  const resetAutofill = useCallback(() => {
    lastFilledRef.current = null;
    lastSettledRef.current = null;
  }, []);

  return {resetAutofill};
}
